import React from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * BestandsverlaufChart — vertikale CSS-Balken fuer 12-Monats-Verlauf.
 * Keine externe Chart-Bibliothek, nur div mit berechneter Hoehe.
 * Props:
 *   bestandsverlauf  { source: string, data: Array<{ monat: string, wert: number }> }
 */
const BestandsverlaufChart = ({ bestandsverlauf }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!bestandsverlauf) return null;

  const data = Array.isArray(bestandsverlauf.data) ? bestandsverlauf.data : [];
  const isMock = bestandsverlauf.source === 'mock';

  const sectionCls = isDarkMode
    ? 'bg-white/6 border border-white/12 rounded-2xl p-5'
    : 'bg-white border border-slate-200 rounded-2xl p-5 shadow-sm';

  const titleCls = isDarkMode
    ? 'text-sm font-semibold text-white/80 mb-4'
    : 'text-sm font-semibold text-slate-700 mb-4';

  const mockBadge = isDarkMode
    ? 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 ml-2'
    : 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 ml-2';

  const labelCls = isDarkMode ? 'text-[10px] text-white/40 mt-1' : 'text-[10px] text-slate-400 mt-1';
  const barBg = isDarkMode
    ? 'bg-gradient-to-t from-blue-600 via-blue-500 to-cyan-400'
    : 'bg-gradient-to-t from-brand via-blue-500 to-cyan-400';
  const tooltipCls = isDarkMode
    ? 'text-[10px] text-white/60 absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap'
    : 'text-[10px] text-slate-500 absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap';

  if (data.length === 0) {
    return (
      <div className={sectionCls}>
        <div className={titleCls}>
          {t('lm.dashVerlauf')}
          {isMock && <span className={mockBadge}>{t('lm.mockBadge')}</span>}
        </div>
        <p className={isDarkMode ? 'text-sm text-white/40' : 'text-sm text-slate-400'}>{t('lm.dashEmpty')}</p>
      </div>
    );
  }

  const maxWert = Math.max(...data.map((d) => d.wert), 1);
  const BAR_HEIGHT = 140; // px Maxhoehe

  const formatMonat = (iso) => {
    const [year, month] = iso.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
  };

  const formatEurShort = (val) =>
    val >= 1000
      ? `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(val / 1000)} T€`
      : `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(val)} €`;

  return (
    <div className={sectionCls}>
      {/* Mock-Badge: Mock-Daten muessen sichtbar als solche markiert sein (HANDOVER.md). */}
      <div className={titleCls}>
        {t('lm.dashVerlauf')}
        {isMock && <span className={mockBadge}>{t('lm.mockBadge')}</span>}
      </div>
      <div className="flex items-end gap-1.5 overflow-x-auto pb-1" style={{ minHeight: BAR_HEIGHT + 28 }}>
        {data.map((d, i) => {
          const barH = Math.max(Math.round((d.wert / maxWert) * BAR_HEIGHT), 4);
          return (
            <div key={i} className="flex flex-col items-center flex-1 min-w-[30px] relative">
              {/* Wert-Label ueber Balken */}
              <div className={tooltipCls}>{formatEurShort(d.wert)}</div>
              <div
                className={`${barBg} rounded-t-md w-full opacity-90 hover:opacity-100 shadow-sm transition-all`}
                style={{ height: barH }}
              />
              <div className={labelCls}>{formatMonat(d.monat)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BestandsverlaufChart;
